import React, { useState, useCallback, useRef } from 'react'
import { token } from '@atlaskit/tokens'
import Button from '@atlaskit/button'
import Spinner from '@atlaskit/spinner'
import AttachmentIcon from '@atlaskit/icon/glyph/attachment'
import {
  MAX_FILE_SIZE_MB,
  isBlockedFileType,
  validateFileSize,
  formatFileSize
} from '../../utils/formatters'
import { useClipboardPaste } from '../../hooks/useClipboardPaste'

/**
 * FILE UPLOAD COMPONENT
 *
 * Drag-and-drop file upload zone with validation.
 *
 * Features:
 * - Drag-and-drop support
 * - Click to browse files
 * - File size validation (max 50 MB)
 * - Blocked file type validation
 * - Upload progress indicator
 * - Multiple file upload support
 *
 * @param {function} onUpload - Callback when file is selected (receives File object)
 * @param {boolean} multiple - Allow multiple file selection (default: false)
 * @param {boolean} disabled - Disable the upload zone
 * @param {boolean} isActive - Enable clipboard paste listening (default: true)
 */
export function FileUpload({
  onUpload,
  multiple = false,
  disabled = false,
  isActive = true
}) {
  // State for drag-over visual feedback
  const [isDragOver, setIsDragOver] = useState(false)

  // State for upload progress
  const [isUploading, setIsUploading] = useState(false)
  const [uploadingFileName, setUploadingFileName] = useState('')

  // State for validation errors
  const [error, setError] = useState('')

  // Ref for hidden file input
  const fileInputRef = useRef(null)

  /**
   * VALIDATE FILE
   * Checks file size and type before upload.
   * Returns error message or empty string if valid.
   */
  const validateFile = useCallback((file) => {
    // Check file size
    if (!validateFileSize(file)) {
      return `File "${file.name}" exceeds maximum size of ${MAX_FILE_SIZE_MB} MB`
    }

    // Check blocked file types
    if (isBlockedFileType(file.name)) {
      return `File type "${file.name.split('.').pop()}" is not allowed`
    }

    return ''
  }, [])

  /**
   * HANDLE FILE SELECTION
   * Validates and uploads the selected file(s).
   */
  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0 || disabled) return

    setError('')

    // Process each file
    for (const file of files) {
      // Validate file
      const validationError = validateFile(file)
      if (validationError) {
        setError(validationError)
        continue
      }

      // Upload file
      setIsUploading(true)
      setUploadingFileName(file.name)

      try {
        await onUpload(file)
      } catch (err) {
        console.error('Failed to upload file:', err)
        setError(`Failed to upload "${file.name}". ${err.message || 'Please try again.'}`)
      } finally {
        setIsUploading(false)
        setUploadingFileName('')
      }

      // If not multiple, only process first file
      if (!multiple) break
    }
  }, [disabled, validateFile, onUpload, multiple])

  // Clipboard paste support - only active when isActive is true
  const { isPasting } = useClipboardPaste({
    onPaste: handleFiles,
    isActive,
    disabled: disabled || isUploading
  })

  /**
   * HANDLE FILE INPUT CHANGE
   */
  const handleInputChange = useCallback((e) => {
    const files = e.target.files
    if (files) {
      handleFiles(Array.from(files))
    }
    // Reset input value so same file can be selected again
    e.target.value = ''
  }, [handleFiles])

  /**
   * HANDLE CLICK
   * Opens file browser dialog.
   */
  const handleClick = useCallback(() => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click()
    }
  }, [disabled, isUploading])

  /**
   * HANDLE DRAG EVENTS
   */
  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled && !isUploading) {
      setIsDragOver(true)
    }
  }, [disabled, isUploading])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled && !isUploading) {
      setIsDragOver(true)
    }
  }, [disabled, isUploading])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    if (disabled || isUploading) return

    const files = e.dataTransfer?.files
    if (files) {
      handleFiles(Array.from(files))
    }
  }, [disabled, isUploading, handleFiles])

  // Determine zone styles based on state
  const zoneStyles = {
    padding: '20px',
    borderRadius: '4px',
    border: `2px dashed ${
      isDragOver || isPasting
        ? token('color.border.brand', '#0052CC')
        : error
        ? token('color.border.danger', '#DE350B')
        : token('color.border', '#DFE1E6')
    }`,
    backgroundColor: isDragOver || isPasting
      ? token('color.background.selected', '#DEEBFF')
      : token('color.background.neutral', '#F4F5F7'),
    cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease',
    textAlign: 'center'
  }

  return (
    <div className="file-upload">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleInputChange}
        multiple={multiple}
        style={{ display: 'none' }}
        disabled={disabled || isUploading}
      />

      {/* Dropzone */}
      <div
        className="file-upload-zone"
        style={zoneStyles}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        role="button"
        tabIndex={disabled || isUploading ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick()
          }
        }}
        aria-label="Upload file"
      >
        {isUploading ? (
          // Uploading state
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <Spinner size="medium" />
            <span
              style={{
                fontSize: '13px',
                color: token('color.text', '#172B4D')
              }}
            >
              Uploading {uploadingFileName}...
            </span>
          </div>
        ) : (
          // Default state
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <AttachmentIcon
              size="medium"
              primaryColor={token('color.icon.subtle', '#6B778C')}
              label="Upload"
            />
            <span
              style={{
                fontSize: '13px',
                color: token('color.text', '#172B4D')
              }}
            >
              {isDragOver ? 'Drop file here' : isPasting ? 'Processing pasted image...' : 'Drop file here, paste an image, or click to upload'}
            </span>
            <span
              style={{
                fontSize: '11px',
                color: token('color.text.subtle', '#5E6C84')
              }}
            >
              Maximum file size: {MAX_FILE_SIZE_MB} MB
            </span>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div
          style={{
            marginTop: '8px',
            fontSize: '12px',
            color: token('color.text.danger', '#DE350B')
          }}
        >
          {error}
        </div>
      )}
    </div>
  )
}

export default FileUpload
